import { useEffect, useState, Fragment } from "react";
import classnames from "classnames";
import useSWR from "swr";
import { Listbox, Transition } from "@headlessui/react";
import { CheckIcon, SelectorIcon } from "@heroicons/react/solid";
import Card from "components/global/card";
import { formatNumber } from "utils/intl";
import rideTypes from "utils/rideTypes";

import ATTRACTIONS_QUERY from "queries/attractions";

function Attractions({ manufacturer }) {
  const [currentPage, setCurrentPage] = useState(1);
  const [currentTypeFilter, setCurrentTypeFilter] = useState("");
  const [nbPages, setNbPages] = useState(0);
  const [selected, setSelected] = useState("");

  const perPage = 30;
  const startRange = perPage * (currentPage - 1);
  const variables = {
    limit: perPage,
    start: startRange,
  };

  if (manufacturer) {
    variables.manufacturer = manufacturer;
  }

  if (currentTypeFilter) {
    variables.type = currentTypeFilter;
  }

  const { data, error } = useSWR([
    ATTRACTIONS_QUERY(!!manufacturer, !!currentTypeFilter),
    variables,
  ]);

  if (error) {
    console.error(error);
  }

  useEffect(() => {
    setNbPages(Math.max(data?.attractionsCount / perPage));
  }, [data?.attractionsCount]);

  if (!Array.isArray(data?.attractions)) {
    return null;
  }

  return (
    <>
      <div className="mb-8 flex w-full">
        <p className="flex-grow self-center text-lg text-gray-700">
          Showing{" "}
          <span className="font-mono">
            {formatNumber(startRange + 1)}-
            {formatNumber(
              Math.min(startRange + perPage, data?.attractionsCount)
            )}
          </span>{" "}
          of{" "}
          <span className="font-mono">
            {formatNumber(data?.attractionsCount)}
          </span>
        </p>
        <div className="flex-grow justify-end text-gray-700">
          <Listbox
            value={selected}
            onChange={(value) => {
              setSelected(value);
              setCurrentTypeFilter(value);
              setCurrentPage(1);
            }}
          >
            {({ open }) => (
              <>
                <Listbox.Label className="sr-only block text-sm font-medium text-gray-700">
                  Type:
                </Listbox.Label>
                <div className="relative">
                  <Listbox.Button className="relative w-full cursor-default rounded-md border border-gray-300 bg-white py-2 pl-3 pr-10 text-left shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 sm:text-sm">
                    <span className="block truncate">
                      {selected ? rideTypes[selected].name : "All"}
                    </span>
                    <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                      <SelectorIcon
                        className="h-5 w-5 text-gray-400"
                        aria-hidden="true"
                      />
                    </span>
                  </Listbox.Button>

                  <Transition
                    show={open}
                    as={Fragment}
                    leave="transition ease-in duration-100"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                  >
                    <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                      <Listbox.Option
                        key="ride-type-all"
                        className={({ active }) =>
                          classnames(
                            active
                              ? "bg-indigo-600 text-white"
                              : "text-gray-900",
                            "relative cursor-default select-none py-2 pl-3 pr-9"
                          )
                        }
                        value=""
                      >
                        {({ selected, active }) => (
                          <>
                            <span
                              className={classnames(
                                selected ? "font-semibold" : "font-normal",
                                "block truncate"
                              )}
                            >
                              All
                            </span>

                            {selected ? (
                              <span
                                className={classnames(
                                  active ? "text-white" : "text-indigo-600",
                                  "absolute inset-y-0 right-0 flex items-center pr-4"
                                )}
                              >
                                <CheckIcon
                                  className="h-5 w-5"
                                  aria-hidden="true"
                                />
                              </span>
                            ) : null}
                          </>
                        )}
                      </Listbox.Option>
                      {Object.keys(rideTypes).map((type) => {
                        if (type === "OTHER") {
                          return null;
                        }

                        return (
                          <Listbox.Option
                            key={`ride-type-${type}`}
                            className={({ active }) =>
                              classnames(
                                active
                                  ? "bg-indigo-600 text-white"
                                  : "text-gray-900",
                                "relative cursor-default select-none py-2 pl-3 pr-9"
                              )
                            }
                            value={type}
                          >
                            {({ selected, active }) => (
                              <>
                                <span
                                  className={classnames(
                                    selected ? "font-semibold" : "font-normal",
                                    "block truncate"
                                  )}
                                >
                                  {rideTypes[type].name}
                                </span>

                                {selected ? (
                                  <span
                                    className={classnames(
                                      active ? "text-white" : "text-indigo-600",
                                      "absolute inset-y-0 right-0 flex items-center pr-4"
                                    )}
                                  >
                                    <CheckIcon
                                      className="h-5 w-5"
                                      aria-hidden="true"
                                    />
                                  </span>
                                ) : null}
                              </>
                            )}
                          </Listbox.Option>
                        );
                      })}
                    </Listbox.Options>
                  </Transition>
                </div>
              </>
            )}
          </Listbox>
        </div>
      </div>
      <div className="mb-10 grid grid-cols-2 gap-8 md:grid-cols-3 lg:grid-cols-4">
        {data?.attractions?.map((attraction) => {
          if (!attraction.Park) {
            console.warn("MISSING PARK", attraction);
            return null;
          }
          return (
            <Card
              link={`/park/${attraction.Park.Slug}/${attraction.Slug}`}
              title={attraction.Name}
              image={attraction.MainPhoto}
              type="attraction"
              additional={attraction.Park.Name}
              key={`attraction-${attraction.id}`}
            />
          );
        })}
      </div>
      <ul className="flex justify-center">
        <li
          className={classnames(
            "mx-1 select-none rounded-lg px-3 py-2",
            currentPage === 1
              ? "bg-indigo-100 text-gray-500"
              : "bg-indigo-900 text-white"
          )}
          key="pagination-previous"
        >
          <button
            type="button"
            className={classnames(
              "flex items-center font-bold",
              currentPage === 1 ? "cursor-default" : ""
            )}
            onClickCapture={(event) => {
              event.preventDefault();
              if (currentPage === 1) {
                return false;
              }
              setCurrentPage(currentPage - 1);
              window.scroll(0, 0);
            }}
          >
            <span className="mx-1">previous</span>
          </button>
        </li>
        <li
          className={classnames(
            "mx-1 select-none rounded-lg px-3 py-2",
            currentPage >= nbPages
              ? "bg-indigo-100 text-gray-500"
              : "bg-indigo-900 text-white"
          )}
        >
          <button
            type="button"
            className={classnames(
              "flex items-center font-bold",
              currentPage >= nbPages ? "cursor-default" : ""
            )}
            onClick={(event) => {
              event.preventDefault();
              if (currentPage >= nbPages) {
                return false;
              }
              setCurrentPage(currentPage + 1);
              window.scroll(0, 0);
            }}
          >
            <span className="mx-1">next</span>
          </button>
        </li>
      </ul>
    </>
  );
}

export default Attractions;
